// src/pages/HomePage.jsx
import React from 'react';
import { Link } from 'react-router-dom';

import { useFeaturedBooks } from '../hooks/useFeaturedBooks';
import { useBestSellers } from '../hooks/useBestSellers';
import { useNewArrivals } from '../hooks/useNewArrivals';

import BookGrid from '../components/book/BookGrid';
import LoadingSpinner from '../components/common/LoadingSpinner';

const HomePage = () => {
  const { books: featuredBooks, loading: featuredLoading } = useFeaturedBooks();
  const { books: bestSellers, loading: bestSellersLoading } = useBestSellers();
  const { books: newArrivals, loading: newArrivalsLoading } = useNewArrivals();

  const isLoading = featuredLoading || bestSellersLoading || newArrivalsLoading;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="home-page">
      {/* ================= HERO ================= */}
      <section className="hero">
        <div className="hero-content">
          <span className="hero-badge">Nepali Literature in Canada</span>
          <h1 className="hero-title">
            Stories that connect <br />
            <span>Nepal & Canada</span>
          </h1>
          <p className="hero-description">
            Discover authentic Nepali novels, carefully curated for readers who
            want to stay connected to their roots while living in Canada.
          </p>
          <div className="hero-actions">
            <Link to="/shop" className="btn-primary">
              Shop All Books
            </Link>
            <Link to="/featured" className="btn-secondary">
              Explore Featured
            </Link>
          </div>
        </div>
      </section>

      {/* ================= FEATURED ================= */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Featured Novels</h2>
              <p className="section-subtitle">Handpicked stories for every reader</p>
            </div>
            <Link to="/featured" className="view-all">
              View all →
            </Link>
          </div>

          {featuredBooks.length > 0 ? (
            <BookGrid books={featuredBooks.slice(0, 8)} />
          ) : (
            <p className="empty-message">No featured books available right now.</p>
          )}
        </div>
      </section>

      {/* ================= BEST SELLERS ================= */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Best Sellers</h2>
              <p className="section-subtitle">Most loved by our community</p>
            </div>
            <Link to="/bestsellers" className="view-all">
              View all →
            </Link>
          </div>

          {bestSellers.length > 0 ? (
            <BookGrid books={bestSellers.slice(0, 8)} />
          ) : (
            <p className="empty-message">No best sellers listed yet.</p>
          )}
        </div>
      </section>

      {/* ================= NEW ARRIVALS ================= */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">New Arrivals</h2>
              <p className="section-subtitle">Fresh stories just added</p>
            </div>
            <Link to="/new-arrivals" className="view-all">
              View all →
            </Link>
          </div>

          {newArrivals.length > 0 ? (
            <BookGrid books={newArrivals.slice(0, 8)} />
          ) : (
            <p className="empty-message">No new arrivals at the moment.</p>
          )}
        </div>
      </section>

      {/* ================= WHY DKL ================= */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title centered">Why Readers Choose DKL Books</h2>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">📚</div>
              <h3>Authentic Collection</h3>
              <p>Carefully selected Nepali novels from classic to contemporary writers.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">🇨🇦</div>
              <h3>Canada-Wide Delivery</h3>
              <p>Fast and reliable shipping across Toronto and all of Canada.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">❤️</div>
              <h3>Community Focused</h3>
              <p>Built by Nepalis, for the Nepali community living abroad.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title centered">What Our Readers Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p>
                “The best place to find authentic Nepali books in Canada.
                Beautifully curated and delivered with care.”
              </p>
              <span>— Raj K., Toronto</span>
            </div>
            
            <div className="testimonial-card">
              <p>
                “A wonderful collection of classics and new releases.
                Highly recommended!”
              </p>
              <span>— Bishal M., Scarborough</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to rediscover Nepali literature?</h2>
          <p>Browse our full collection of novels and start reading today.</p>
          <Link to="/shop" className="btn-primary">
            Explore the Shop
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;